/**
 * Bridges a click-to-dial action to its resulting Five9 call so the call
 * can be registered against the dispatch it was placed from.
 *
 * `ClickToDialPhone` arms a pending entry just before invoking the SDK's
 * `click2dial`. When the resulting OUTBOUND call shows up on the lifecycle
 * stream with full identity (sessionId/interactionId/callId) and a dialed
 * number matching the pending entry, we POST to the dispatch-qore
 * click2dial-register endpoint, which upserts the call, attaches it to the
 * dispatch (session.DispatchCall) and pre-registers its
 * CallTranscriptsRecord so the AI transcript lands in dispatch history.
 *
 * Module-level singleton: only the Five9 owner tab can dial (enforced by
 * the caller via `getInteractionApi()`), and only one dial can be pending
 * at a time — a new arm is refused until the current one completes, is
 * cleared, or expires (number-based matching can't keep two live dials apart).
 */
import { registerClick2DialCall } from 'src/services/five9CallsService';
import { store } from 'src/store';
import { associateDispatch } from 'src/actions/five9Actions';
import { subscribeToFive9CallLifecycleEvents } from './five9CallLifecycleEvents';
import extractCall from './extractCall';
import toDialableNumber from './toDialableNumber';
import logger from './five9Logger';

/** Events that can carry the outbound call's identity. */
const MATCHABLE_EVENTS = new Set([
  'interaction.callStarted',
  'interaction.callAccepted',
  'interaction.callEnded',
  'crm.saveLog'
]);

/** A dial that never produces a matching call expires after this. */
export const PENDING_CLICK2DIAL_TTL_MS = 2 * 60 * 1000;

/**
 * Post-dial no-match window. Five9 dial failures often surface only as an
 * ADT toast (no promise rejection, no lifecycle event), so after `click2dial`
 * returns the entry is disarmed on this much shorter timer unless a matching
 * outbound call is observed first.
 */
export const PENDING_CLICK2DIAL_POST_DIAL_TTL_MS = 10 * 1000;

/** @type {{ dispatchId: number, number: string, expiresAt: number, sawStarted?: boolean, matchObserved?: boolean, matchedFive9CallId?: string }|null} */
let pending = null;
/** @type {(() => void)|null} */
let unsubscribe = null;
/**
 * The entry whose registration POST is outstanding. Scoped to the entry (not
 * a global flag) so duplicate events for the SAME click are de-duped without
 * blocking a newer superseding click from registering independently.
 * @type {object|null}
 */
let inFlightFor = null;
/** @type {ReturnType<typeof setTimeout>|null} */
let postDialExpiryTimer = null;

const cancelPostDialExpiry = () => {
  if (postDialExpiryTimer) {
    clearTimeout(postDialExpiryTimer);
    postDialExpiryTimer = null;
  }
};

const currentPending = () => {
  if (pending && Date.now() > pending.expiresAt) {
    logger.warn('click2dial.pendingExpired', { dispatchId: pending.dispatchId });
    pending = null;
  }
  return pending;
};

const handleLifecycleEvent = ({ type, payload }) => {
  const armed = currentPending();
  if (!armed || inFlightFor === armed || !MATCHABLE_EVENTS.has(type)) return;
  const call = extractCall(payload);
  if (!call || call.direction !== 'OUTBOUND') return;
  const dialed = [call.dnis, call.number, call.phoneNumber]
    .map(toDialableNumber)
    .find(Boolean);
  if (!dialed || dialed !== armed.number) return;
  // Only a fresh callStarted (post-arm) opens the bridge: a PRE-EXISTING
  // same-number call's accepted/ended/saveLog events must not establish a
  // match, or its identity would be registered against this dispatch.
  if (type === 'interaction.callStarted') armed.sawStarted = true;
  if (!armed.sawStarted) return;
  // The dialed call exists — the short post-dial expiry no longer applies
  // (the full TTL still bounds identity waits and POST retries). matchObserved
  // also makes a LATER schedulePendingClick2DialExpiry a no-op: the match can
  // arrive while the dialer is still awaiting click2dial.
  armed.matchObserved = true;
  cancelPostDialExpiry();
  // The register endpoint needs full identity; a later event will carry it.
  if (!call.five9SessionId || !call.five9InteractionId || !call.five9CallId) return;
  // Retries are pinned to the FIRST fully-identified matching call: after a
  // failed POST, a different outbound call to the same number must not be
  // registered against this dispatch.
  if (armed.matchedFive9CallId && armed.matchedFive9CallId !== call.five9CallId) return;
  if (!armed.matchedFive9CallId) armed.matchedFive9CallId = call.five9CallId;
  const body = {
    dispatchId: armed.dispatchId,
    five9SessionId: call.five9SessionId,
    five9InteractionId: call.five9InteractionId,
    five9CallId: call.five9CallId,
    dialedNumber: dialed
  };
  const matchedCallSid = call.callSid;
  logger.event('click2dial.registering', body);
  // `pending` stays armed until the POST succeeds so the next matchable
  // lifecycle event retries a transient backend failure (within TTL); the
  // backend endpoint is idempotent.
  inFlightFor = armed;
  registerClick2DialCall(body)
    .then(() => {
      // Only disarm the entry this POST was for — a newer click may have
      // superseded it while the request was outstanding.
      if (pending === armed) pending = null;
      // Mirror into redux so UI affordances (Call Linked state, reverse
      // lookups) see the server-side association, same as the /create flow.
      if (matchedCallSid) {
        store.dispatch(associateDispatch(matchedCallSid, body.dispatchId));
      }
      logger.event('click2dial.registered', {
        dispatchId: body.dispatchId,
        five9CallId: body.five9CallId
      });
    })
    .catch((err) => {
      const status = err?.response?.status;
      // 4xx (minus 408/429) means the request itself is bad — retrying the
      // same body is pointless and risks outliving its relevance; disarm.
      const isTerminal = status >= 400 && status < 500 && status !== 408 && status !== 429;
      if (isTerminal && pending === armed) pending = null;
      logger[isTerminal ? 'error' : 'warn']('click2dial.registerFailed', {
        dispatchId: body.dispatchId,
        five9CallId: body.five9CallId,
        message: err?.message,
        status: status ?? null,
        willRetry: !isTerminal
      });
    })
    .finally(() => {
      if (inFlightFor === armed) inFlightFor = null;
    });
};

/**
 * Arm the registry for an imminent `click2dial`. Lazily subscribes to the
 * lifecycle stream on first use.
 *
 * Refuses to arm while ANY registration is pending/in flight — not just the
 * same number: lifecycle matching is number-based until identity is pinned,
 * so a second live dial could strand the first call's registration (its
 * events would find no entry) or bind the wrong identity. Callers must skip
 * the dial when this returns false.
 *
 * @param {{ dispatchId: number|string, number: string }} params
 * @returns {object|false} the armed entry (opaque token for
 *   schedulePendingClick2DialExpiry/clearPendingClick2Dial) when armed; false
 *   when rejected (invalid input or another registration is already
 *   pending/in flight).
 */
export const registerPendingClick2Dial = ({ dispatchId, number }) => {
  // Purge an expired entry first so a stale dial can't block re-arming.
  currentPending();
  const dialable = toDialableNumber(number);
  const normalizedDispatchId = Number(dispatchId);
  if (!dialable || !Number.isFinite(normalizedDispatchId) || normalizedDispatchId <= 0) {
    return false;
  }
  if (pending || inFlightFor) {
    logger.warn('click2dial.pendingExists', {
      dispatchId: normalizedDispatchId,
      number: dialable,
      pendingDispatchId: (pending ?? inFlightFor)?.dispatchId ?? null
    });
    return false;
  }
  pending = {
    dispatchId: normalizedDispatchId,
    number: dialable,
    expiresAt: Date.now() + PENDING_CLICK2DIAL_TTL_MS
  };
  if (!unsubscribe) unsubscribe = subscribeToFive9CallLifecycleEvents(handleLifecycleEvent);
  return pending;
};

export const getPendingClick2Dial = () => currentPending();

/**
 * Start the post-dial no-match window for a SPECIFIC armed entry. Called by
 * the dialer right after `click2dial` returns (it resolves void even when
 * the dial fails). No-op when the entry is no longer the armed one or already
 * saw a matching call — a stale token from an earlier (cleared/expired)
 * attempt must never expire the currently armed entry. The first matching
 * event cancels it.
 *
 * @param {object} entry - token returned by registerPendingClick2Dial
 * @param {number} [timeoutMs]
 */
export const schedulePendingClick2DialExpiry = (
  entry,
  timeoutMs = PENDING_CLICK2DIAL_POST_DIAL_TTL_MS
) => {
  if (!entry || pending !== entry || entry.matchObserved) return;
  cancelPostDialExpiry();
  postDialExpiryTimer = setTimeout(() => {
    postDialExpiryTimer = null;
    if (pending === entry && !entry.matchObserved && inFlightFor !== entry) {
      logger.warn('click2dial.pendingExpired', {
        dispatchId: entry.dispatchId,
        reason: 'no matching call after dial'
      });
      pending = null;
    }
  }, timeoutMs);
};

/**
 * Disarm a pending dial on known failure paths so a stale entry can't attach
 * a later call to the wrong dispatch. With an entry token (from
 * registerPendingClick2Dial) it only disarms THAT entry — a stale token from
 * an earlier attempt can't clear whatever armed after it. Without a token it
 * disarms unconditionally (ADT-level disable). Keeps the lifecycle
 * subscription.
 *
 * @param {object} [entry]
 */
export const clearPendingClick2Dial = (entry) => {
  if (entry !== undefined && pending !== entry) return;
  pending = null;
  cancelPostDialExpiry();
};

/**
 * Full reset: drop pending state and the stream subscription. Called from
 * the Five9 auth-transition purge (logout / user switch) alongside
 * `resetFive9()`, and from tests.
 */
export const resetClick2DialRegistration = () => {
  pending = null;
  inFlightFor = null;
  cancelPostDialExpiry();
  if (unsubscribe) {
    unsubscribe();
    unsubscribe = null;
  }
};
